"use strict";
/**
 * Message formatting utilities for error messages
 *
 * Handles interpolation of context values into message templates
 * and supports both development and production modes
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatErrorMessage = formatErrorMessage;
exports.getErrorMessageTemplate = getErrorMessageTemplate;
exports.formatContext = formatContext;
var messages_1 = require("./messages");
/**
 * Check if we're in development mode
 * Falls back to checking NODE_ENV if __DEV__ is not defined
 */
function isDevelopmentMode() {
    var _a;
    try {
        return __DEV__;
    }
    catch (_b) {
        return typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV) !== 'production';
    }
}
/**
 * Interpolate context values into a message template
 *
 * Replaces $variableName with the corresponding value from context
 *
 * @param template - Message template with $variable placeholders
 * @param context - Context object containing values to interpolate
 * @returns Formatted message with interpolated values
 */
function interpolateMessage(template, context) {
    if (!context) {
        // Remove any remaining $variable placeholders
        return template.replace(/\$\w+/g, '');
    }
    return template.replace(/\$(\w+)/g, function (match, variableName) {
        var value = context[variableName];
        if (value === undefined || value === null) {
            // Keep the placeholder if no value available
            return '';
        }
        // Handle arrays specially
        if (Array.isArray(value)) {
            return value.join(', ');
        }
        // Convert to string
        return String(value);
    });
}
/**
 * Format an error message for a given error code and context
 *
 * In development mode: Returns fully interpolated message
 * In production mode: Returns compact error code reference
 *
 * @param code - The error code
 * @param context - Optional context data for interpolation
 * @returns Formatted error message
 */
function formatErrorMessage(code, context) {
    var messageTemplate = messages_1.HERMIS_ERROR_MESSAGES[code];
    if (!messageTemplate) {
        return "Unknown error code: ".concat(code);
    }
    // In development mode, show full interpolated message
    if (isDevelopmentMode()) {
        return interpolateMessage(messageTemplate, context);
    }
    // In production mode, show compact error code reference
    // Users can decode this using the CLI tool or documentation
    return "Hermis Error ".concat(code, ". Decode at: https://hermis.dev/errors/").concat(code);
}
/**
 * Get the raw message template for an error code
 * Useful for testing or debugging
 *
 * @param code - The error code
 * @returns Raw message template
 */
function getErrorMessageTemplate(code) {
    return messages_1.HERMIS_ERROR_MESSAGES[code] || "Unknown error code: ".concat(code);
}
/**
 * Format context data for logging
 * Converts context object to a readable string
 *
 * @param context - Context data
 * @returns Formatted context string
 */
function formatContext(context) {
    if (!context || Object.keys(context).length === 0) {
        return '';
    }
    var entries = Object.entries(context)
        .filter(function (_a) {
        var _ = _a[0], value = _a[1];
        return value !== undefined && value !== null;
    })
        .map(function (_a) {
        var key = _a[0], value = _a[1];
        if (Array.isArray(value)) {
            return "".concat(key, ": [").concat(value.join(', '), "]");
        }
        return "".concat(key, ": ").concat(value);
    });
    return entries.length > 0 ? "{ ".concat(entries.join(', '), " }") : '';
}
